import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Save, User, Shield, Key, Eye, EyeOff, Building } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PERMISSIONS_DEFS } from './UsersManagement/RolesAndPermissionsView';
import { usePharmacies } from '../context/PharmacyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  super_admin: 'Super Administrateur',
  admin: 'Administrateur',
  gerant: 'Gérant',
  comptable: 'Comptable',
  caissier: 'Caissier',
  assistant: 'Assistant',
  user: 'Utilisateur',
  custom: 'Personnalisé',
};

function Profile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Forms
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    avatar_url: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [permissions, setPermissions] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);

  // Hooks must be called before any early return
  const { userPharmacies } = usePharmacies();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      if (!stored) return;
      const user = JSON.parse(stored);
      
      // Fetch fresh data (don't fetch /pharmacies as standard users can't access it)
      const headers = { 'X-User-Id': user.id };
      const [userRes, permsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers, params: { id: user.id } }),
        axios.get(`${API}/users/${user.id}/granular-permissions`, { headers })
      ]);
      
      const freshUser = userRes.data.find(u => u.id === user.id) || user;
      setCurrentUser(freshUser);
      setProfileData({
        full_name: freshUser.full_name || '',
        email: freshUser.email || '',
        phone: freshUser.phone || '',
        address: freshUser.address || '',
        username: freshUser.username || '',
        avatar_url: freshUser.avatar_url || ''
      });
      
      setPermissions(permsRes.data.granular_permissions);
      
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté (utilisez JPG, PNG, WEBP)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const headers = { 'X-User-Id': currentUser.id };
      const res = await axios.post(`${API}/upload-avatar`, formData, { headers });
      
      const newAvatarUrl = res.data.url;
      
      // Update profile
      await axios.put(`${API}/users/${currentUser.id}/profile`, {
        avatar_url: newAvatarUrl
      }, { headers });
      
      setProfileData(prev => ({ ...prev, avatar_url: newAvatarUrl }));
      
      // Update session storage so header updates
      const stored = JSON.parse(sessionStorage.getItem('pharmacy_user'));
      stored.avatar_url = newAvatarUrl;
      sessionStorage.setItem('pharmacy_user', JSON.stringify(stored));
      
      toast.success('Photo de profil mise à jour');
      // Trigger a refresh for the header
      window.dispatchEvent(new Event('storage'));
      
    } catch (error) {
      toast.error('Erreur lors du téléchargement de l\'image');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = { 'X-User-Id': currentUser.id };
      await axios.put(`${API}/users/${currentUser.id}/profile`, profileData, { headers });
      toast.success('Profil mis à jour');
      
      // Update session storage
      const stored = JSON.parse(sessionStorage.getItem('pharmacy_user'));
      const updated = { ...stored, ...profileData };
      sessionStorage.setItem('pharmacy_user', JSON.stringify(updated));
      window.dispatchEvent(new Event('storage'));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    try {
      const headers = { 'X-User-Id': currentUser.id };
      await axios.put(`${API}/users/${currentUser.id}/password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      }, { headers });
      
      toast.success('Mot de passe mis à jour avec succès');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement du profil...</div>;

  // Get the primary pharmacy name from context or fallback to legacy currentUser properties
  const pharmacyName = userPharmacies?.[0]?.pharmacy_name || currentUser?.pharmacies?.[0]?.pharmacy_name || 'Non assignée';

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-6 pb-12 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
          <p className="text-gray-500 mt-1">Gérez vos informations personnelles et vos accès</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Colonne Gauche: Avatar & Info Résumé */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col items-center text-center">
            <div className="relative mb-4 group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              {profileData.avatar_url ? (
                <img 
                  src={`${BACKEND_URL}${profileData.avatar_url}`} 
                  alt="Avatar" 
                  className="w-32 h-32 rounded-full object-cover border-4 border-emerald-50 shadow-md"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-4xl font-bold shadow-md">
                  {currentUser?.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="text-white w-8 h-8" />
              </div>
            </div>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarUpload}
            />
            
            <h2 className="text-xl font-bold text-gray-900">{profileData.full_name || profileData.username}</h2>
            <p className="text-gray-500 mb-4">{ROLE_LABELS[currentUser?.role] || currentUser?.role}</p>
            
            <div className="w-full bg-blue-50 text-blue-700 rounded-xl p-4 flex items-start gap-3 text-left">
              <Building className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Pharmacie Principale</p>
                <p className="text-sm opacity-90">{pharmacyName}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-gray-400" /> Sécurité
            </h3>
            <form onSubmit={handleUpdatePassword} className="space-y-4" autoComplete="off">
              <div className="space-y-1.5">
                <Label className="text-sm text-gray-700">Ancien mot de passe</Label>
                <div className="relative">
                  <Input 
                    type={showOldPassword ? "text" : "password"} 
                    required 
                    value={passwordData.old_password}
                    onChange={e => setPasswordData({...passwordData, old_password: e.target.value})}
                    autoComplete="new-password"
                    className="h-10 rounded-xl pr-10"
                  />
                  <button type="button" onClick={() => setShowOldPassword(!showOldPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-gray-700">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input 
                    type={showNewPassword ? "text" : "password"} 
                    required 
                    value={passwordData.new_password}
                    onChange={e => setPasswordData({...passwordData, new_password: e.target.value})}
                    autoComplete="new-password"
                    className="h-10 rounded-xl pr-10"
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-gray-700">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Input 
                    type={showConfirmPassword ? "text" : "password"} 
                    required 
                    value={passwordData.confirm_password}
                    onChange={e => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    autoComplete="new-password"
                    className="h-10 rounded-xl pr-10"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="w-full py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors mt-2">
                Changer le mot de passe
              </button>
            </form>
          </div>
        </div>

        {/* Colonne Droite: Infos & Permissions */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-500" /> Informations Personnelles
            </h3>
            <form onSubmit={handleUpdateProfile} className="space-y-5" autoComplete="off">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Nom Complet</Label>
                  <Input 
                    value={profileData.full_name}
                    onChange={e => setProfileData({...profileData, full_name: e.target.value})}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Nom d'utilisateur</Label>
                  <Input 
                    value={profileData.username}
                    onChange={e => setProfileData({...profileData, username: e.target.value})}
                    className="h-11 rounded-xl bg-gray-50"
                    disabled
                  />
                  <p className="text-xs text-gray-400">Non modifiable pour des raisons de sécurité</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Email</Label>
                  <Input 
                    type="email"
                    value={profileData.email}
                    onChange={e => setProfileData({...profileData, email: e.target.value})}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Téléphone</Label>
                  <Input 
                    value={profileData.phone}
                    onChange={e => setProfileData({...profileData, phone: e.target.value})}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Adresse</Label>
                  <Input 
                    value={profileData.address}
                    onChange={e => setProfileData({...profileData, address: e.target.value})}
                    className="h-11 rounded-xl"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button 
                  type="submit" 
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>

        </div>

      </div>
    </div>
  );
}

export default Profile;
